import { useEffect, useContext } from 'react';
import { AppContext } from '../../context/Context';
import { 
  useAuthStore, 
  useRestrictionsStore, 
  useNavigationStore,
  useTableStore,
  useInfoSidebarStore 
} from '../index';
import { useMigration } from './MigrationProvider';

/**
 * Context-Store Synchronization Hook
 * 
 * This hook ensures that during the migration process:
 * 1. Context API state is synced to Zustand stores when migration flags are enabled
 * 2. Store state changes are reflected back to Context API for components not yet migrated
 * 3. Provides seamless transition without breaking existing functionality
 */
export const useContextStoreSync = () => {
  const { flags } = useMigration();
  const context = useContext(AppContext);
  
  // Store hooks
  const authStore = useAuthStore();
  const restrictionsStore = useRestrictionsStore();
  const navigationStore = useNavigationStore();
  const tableStore = useTableStore();
  const infoSidebarStore = useInfoSidebarStore();

  // Sync Auth State
  useEffect(() => {
    if (!flags.useAuthStore || !context) return;

    // Sync Context -> Store (initial sync)
    if (context.isAuthenticated !== authStore.isAuthenticated) {
      if (context.isAuthenticated) {
        authStore.setUser(context.user);
        authStore.setAuthorization(context.authorization);
        authStore.setGroups(context.groups);
        authStore.setUserApiKey(context.userApiKey);
      }
    }

    // Update restrictions when auth state changes
    if (flags.useRestrictionsStore) {
      restrictionsStore.updateRestrictions(
        authStore.isAuthenticated,
        authStore.groups
      );
    }
  }, [
    flags.useAuthStore,
    flags.useRestrictionsStore,
    context?.isAuthenticated,
    context?.user,
    context?.authorization,
    context?.groups,
    context?.userApiKey,
    authStore.isAuthenticated,
    authStore.user,
    authStore.authorization,
    authStore.groups,
    authStore.userApiKey,
  ]);

  // Sync Navigation State
  useEffect(() => {
    if (!flags.useNavigationStore || !context) return;

    // Sync Context -> Store
    if (context.view !== navigationStore.view) {
      navigationStore.setView(context.view);
    }
    if (context.source !== navigationStore.source) {
      navigationStore.setSource(context.source);
    }
    if (context.tab !== navigationStore.tab) {
      navigationStore.setTab(context.tab);
    }
    if (context.searchItems !== navigationStore.searchItems) {
      navigationStore.setSearchItems(context.searchItems);
    }
    if (context.hasValidParams !== navigationStore.hasValidParams) {
      navigationStore.setHasValidParams(context.hasValidParams);
    }

    // Sync info sidebar state
    if (context.selectedInfoItem !== navigationStore.selectedInfoItem) {
      navigationStore.setSelectedInfoItem(context.selectedInfoItem);
    }
    if (context.geneContent && 
        JSON.stringify(context.geneContent) !== JSON.stringify(navigationStore.geneContent)) {
      navigationStore.setGeneContent(context.geneContent);
    }
    if (context.mappingContent && 
        JSON.stringify(context.mappingContent) !== JSON.stringify(navigationStore.mappingContent)) {
      navigationStore.setMappingContent(context.mappingContent);
    }
  }, [
    flags.useNavigationStore,
    context?.view,
    context?.source,
    context?.tab,
    context?.searchItems,
    context?.hasValidParams,
    context?.selectedInfoItem,
    context?.geneContent,
    context?.mappingContent,
    navigationStore.view,
    navigationStore.source,
    navigationStore.tab,
    navigationStore.searchItems,
    navigationStore.hasValidParams,
    navigationStore.selectedInfoItem,
    navigationStore.geneContent,
    navigationStore.mappingContent,
  ]);

  // Sync Restrictions State
  useEffect(() => {
    if (!flags.useRestrictionsStore || !context) return;

    // Sync Context -> Store
    const contextRestrictions = {
      restrictBrowseAll: context.restrictBrowseAll,
      restrictGridDisplay: context.restrictGridDisplay,
      restrictGridResultCount: context.restrictGridResultCount,
      restrictGraphDisplay: context.restrictGraphDisplay,
      restrictSources: context.restrictSources,
      restrictChemical: context.restrictChemical,
      restrictPathwayDisplay: context.restrictPathwayDisplay,
    };

    const storeRestrictions = {
      restrictBrowseAll: restrictionsStore.restrictBrowseAll,
      restrictGridDisplay: restrictionsStore.restrictGridDisplay,
      restrictGridResultCount: restrictionsStore.restrictGridResultCount,
      restrictGraphDisplay: restrictionsStore.restrictGraphDisplay,
      restrictSources: restrictionsStore.restrictSources,
      restrictChemical: restrictionsStore.restrictChemical,
      restrictPathwayDisplay: restrictionsStore.restrictPathwayDisplay,
    };

    // Only sync if there are differences
    if (JSON.stringify(contextRestrictions) !== JSON.stringify(storeRestrictions)) {
      Object.entries(contextRestrictions).forEach(([key, value]) => {
        const setterName = `set${key.charAt(0).toUpperCase() + key.slice(1)}`;
        if (typeof restrictionsStore[setterName] === 'function') {
          restrictionsStore[setterName](value);
        }
      });
    }
  }, [
    flags.useRestrictionsStore,
    context?.restrictBrowseAll,
    context?.restrictGridDisplay,
    context?.restrictGridResultCount,
    context?.restrictGraphDisplay,
    context?.restrictSources,
    context?.restrictChemical,
    context?.restrictPathwayDisplay,
    restrictionsStore.restrictBrowseAll,
    restrictionsStore.restrictGridDisplay,
    restrictionsStore.restrictGridResultCount,
    restrictionsStore.restrictGraphDisplay,
    restrictionsStore.restrictSources,
    restrictionsStore.restrictChemical,
    restrictionsStore.restrictPathwayDisplay,
  ]);

  return {
    syncStatus: {
      auth: flags.useAuthStore,
      restrictions: flags.useRestrictionsStore,
      navigation: flags.useNavigationStore,
      table: flags.useTableStore,
      infoSidebar: flags.useInfoSidebarStore,
    },
    stores: {
      auth: authStore,
      restrictions: restrictionsStore,
      navigation: navigationStore,
      table: tableStore,
      infoSidebar: infoSidebarStore,
    },
  };
};

/**
 * Hook to get the appropriate state source based on migration flags
 */
export const useStateSource = () => {
  const { flags } = useMigration();
  const context = useContext(AppContext);
  const stores = {
    auth: useAuthStore(),
    restrictions: useRestrictionsStore(),
    navigation: useNavigationStore(),
    table: useTableStore(),
    infoSidebar: useInfoSidebarStore(),
  };

  return {
    // Auth state
    auth: flags.useAuthStore ? stores.auth : {
      isAuthenticated: context?.isAuthenticated || false,
      authorization: context?.authorization || '',
      groups: context?.groups || [],
      user: context?.user || null,
      userApiKey: context?.userApiKey || [],
      authError: context?.authError || '',
      userType: null,
    },
    
    // Restrictions state
    restrictions: flags.useRestrictionsStore ? stores.restrictions : {
      restrictBrowseAll: context?.restrictBrowseAll || true,
      restrictGridDisplay: context?.restrictGridDisplay || true,
      restrictGridResultCount: context?.restrictGridResultCount || true,
      restrictGraphDisplay: context?.restrictGraphDisplay || true,
      restrictSources: context?.restrictSources || false,
      restrictChemical: context?.restrictChemical || true,
      restrictPathwayDisplay: context?.restrictPathwayDisplay || true,
      restrictSort: true, // Not in context
      restrictSortOrder: true, // Not in context
    },
    
    // Navigation state
    navigation: flags.useNavigationStore ? stores.navigation : {
      view: context?.view || '',
      source: context?.source || '',
      tab: context?.tab || '',
      searchItems: context?.searchItems || [],
      hasValidParams: context?.hasValidParams || false,
      selectedInfoItem: context?.selectedInfoItem || null,
      geneContent: context?.geneContent || { isActive: false, geneInfo: { symbol: '', id: null, name: '' } },
      mappingContent: context?.mappingContent || { isActive: false },
    },
    
    // Table and InfoSidebar states (only available in stores)
    table: stores.table,
    infoSidebar: stores.infoSidebar,
    
    // Migration info
    flags,
  };
};